/**
 * RegimeDecisionTally.jsx
 * BerBul AI Trading — Shared Intelligence Component
 *
 * Running tally of PROCEED vs SKIP decisions bucketed by market regime.
 * Answers the question: "Is the bot adapting to current conditions?"
 *   — High SKIP rate in CHOPPY = bot is behaving correctly
 *   — High PROCEED rate in TRENDING = bot is correctly aggressive
 *   — Regime shifts show bot recalibrating in real time
 *
 * Works in both contexts:
 *   TradingCommand       — live session, regimes build up over the day
 *   DailyDetailDashboard — full session review; richer data
 *
 * Props:
 *   liveBars  {Array}   — full bar array with eval_decision + eval_agent_story
 *   compact   {boolean} — compact layout (default false)
 *   showLabel {boolean} — show section label (default true)
 *
 * No external dependencies. Pure BRD design system.
 */

import { useMemo, useState, useRef } from "react";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      "#08101E",
  surface: "#0C1525",
  s2:      "#101B2E",
  s3:      "#0e1828",
  border:  "#1E2D42",
  b2:      "#2a3d56",
  text:    "#E8EDF5",
  muted:   "#6A7D95",
  body:    "#B8C8DC",
  green:   "#00E896",
  cyan:    "#00C8FF",
  amber:   "#FFB340",
  red:     "#FF4466",
  blue:    "#4488FF",
  purple:  "#9966FF",
};

const MONO = "'DM Mono', 'Courier New', monospace";

// ── Regime config ─────────────────────────────────────────────────────────────
// Covers both day_type values (BRD-2, session-level) and regime_classification
// values (LLM per-bar fallback). day_type is preferred — see extractRegime().
const REGIME_CONFIG = {
  // ── day_type values (BRD-2 session-level classification) ─────────────────
  NORMAL:            { color: "#B8C8DC", label: "NORMAL",         expectProceed: true,  note: "Standard session — bot proceeds on high-quality setups" },
  TREND_DAY:         { color: "#00E896", label: "TREND DAY",      expectProceed: true,  note: "Trending conditions — high proceed rate expected" },
  STRONG_TREND_DAY:  { color: "#00E896", label: "STRONG TREND",   expectProceed: true,  note: "Strong trend — bot should be most aggressive" },
  WEAK_TREND_DAY:    { color: "#88D400", label: "WEAK TREND DAY", expectProceed: true,  note: "Directional but soft — some proceed activity expected" },
  EVENT_TREND:       { color: "#00C8FF", label: "EVENT TREND",    expectProceed: true,  note: "Event-driven trend — catalyst present, bot proceeds selectively" },
  EVENT_DAY:         { color: "#00C8FF", label: "EVENT DAY",      expectProceed: false, note: "Event day — elevated uncertainty, bot skips most setups" },
  EVENT_VOLATILE:    { color: "#FFB340", label: "EVENT VOLATILE",  expectProceed: false, note: "Volatile event conditions — mostly skipping expected" },
  RANGE_DAY:         { color: "#4488FF", label: "RANGE DAY",      expectProceed: false, note: "Range-bound — bot waits for breakout confirmation" },
  SIDEWAYS_DAY:      { color: "#9966FF", label: "SIDEWAYS DAY",   expectProceed: false, note: "Sideways chop — mostly skipping expected" },
  CHOPPY_DAY:        { color: "#FF4466", label: "CHOPPY DAY",     expectProceed: false, note: "Choppy conditions — high skip rate is correct" },
  VOLATILE_DAY:      { color: "#FFB340", label: "VOLATILE DAY",   expectProceed: false, note: "High volatility — bot in caution mode" },
  // ── regime_classification values (LLM per-bar fallback) ──────────────────
  TRENDING:          { color: "#00E896", label: "TRENDING",        expectProceed: true,  note: "Trending momentum — proceed rate expected to be high" },
  TRENDING_UP:       { color: "#00E896", label: "TRENDING UP",     expectProceed: true,  note: "Bullish trend — proceed rate expected to be high" },
  TRENDING_DOWN:     { color: "#FF4466", label: "TRENDING DOWN",   expectProceed: true,  note: "Bearish trend — proceed (short) rate expected to be high" },
  WEAK_TREND:        { color: "#88D400", label: "WEAK TREND",      expectProceed: true,  note: "Some proceed activity expected" },
  RANGE_BOUND:       { color: "#4488FF", label: "RANGE BOUND",     expectProceed: false, note: "Selective — fewer entries" },
  SIDEWAYS:          { color: "#9966FF", label: "SIDEWAYS",        expectProceed: false, note: "Mostly skipping expected" },
  CHOPPY:            { color: "#FF4466", label: "CHOPPY",          expectProceed: false, note: "Mostly skipping expected" },
  VOLATILE:          { color: "#FFB340", label: "VOLATILE",        expectProceed: false, note: "High caution mode" },
  // ── fallback ──────────────────────────────────────────────────────────────
  UNKNOWN:           { color: "#6A7D95", label: "UNCLASSIFIED",    expectProceed: null,  note: "Regime not yet determined" },
};

function getRegimeConfig(regime) {
  if (!regime) return REGIME_CONFIG.UNKNOWN;
  // Normalize: spaces → underscores, uppercase, strip non-alpha
  const key = regime.toUpperCase().replace(/ /g, "_").replace(/[^A-Z_]/g, "");
  return REGIME_CONFIG[key] || { color: "#6A7D95", label: regime, expectProceed: null, note: "" };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function isProceed(bar) {
  const dec = bar.eval_decision || bar.decision;
  return dec === "PROCEED" || dec === "BUY";
}

function isSkip(bar) {
  const dec = bar.eval_decision || bar.decision;
  return dec === "SKIP_TRADE" || dec === "SKIP" || dec === "HOLD";
}

// ── Regime extractor ─────────────────────────────────────────────────────────
// Priority order (matches DailyDetailDashboard.js regimeOf):
//   1. regime_summary.day_type           (BRD-2 session-level, most reliable)
//   2. market_snapshot.regime_context.day_type  (BRD-2 alternate path)
//   3. chart_analysis...regime_classification   (LLM per-bar fallback)
//   4. legacy fields (story.regime_history, bar.regime, bar.trend)
function extractRegime(bar) {
  const story = bar?.eval_agent_story;
  if (!story) return null;

  // BRD-2 path 1: regime_summary.day_type
  const rs = story.regime_summary || {};
  if (rs.day_type) return rs.day_type;

  // BRD-2 path 2: market_snapshot.regime_context.day_type
  const rc = (story.market_snapshot || {}).regime_context || {};
  if (rc.day_type) return rc.day_type;

  // Old LLM path: chart_analysis.llm_full_response.regime_analysis
  const ra = story.chart_analysis?.llm_full_response?.regime_analysis?.regime_classification;
  if (ra) return String(ra).toUpperCase();

  // Legacy fields
  const raw = story.regime_history || bar.regime || "";
  if (raw) return String(raw).toUpperCase().slice(0, 20);

  // Last resort: infer from trend/signal
  const trend = (bar.trend || "").toUpperCase();
  if (["BULL", "BULLISH", "UP"].includes(trend))   return "TRENDING_UP";
  if (["BEAR", "BEARISH", "DOWN"].includes(trend)) return "TRENDING_DOWN";
  if (["BUY", "SELL"].includes(trend))              return "TRENDING";

  return null;
}

// ── Trade join helper ─────────────────────────────────────────────────────────
// Matches each PROCEED bar to its closed ledger position by entry timestamp.
// Uses ±90s window (same as DailyDetailDashboard TradeStoryView greedyMatch).
// Returns map: barTimestamp → closedLedgerPosition
function joinProceedsToClosed(liveBars, closedLedgerPositions) {
  if (!closedLedgerPositions?.length) return {};

  const proceedBars = (liveBars || []).filter(b => isProceed(b) && b.t);

  // Build scored pairs
  const pairs = [];
  proceedBars.forEach(bar => {
    const barMs = new Date(bar.t).getTime();
    closedLedgerPositions.forEach(pos => {
      const posMs = new Date(pos.entry_submitted_at_raw || pos.first_entry_time || 0).getTime();
      const delta = Math.abs(barMs - posMs);
      if (delta <= 90000) pairs.push({ bar, pos, delta });
    });
  });

  // Greedy assign: sort by delta, assign without reuse
  pairs.sort((a, b) => a.delta - b.delta);
  const usedBars = new Set();
  const usedPos  = new Set();
  const result   = {};

  pairs.forEach(({ bar, pos, delta }) => {
    if (usedBars.has(bar.t) || usedPos.has(pos.entry_submitted_at_raw || pos.first_entry_time)) return;
    result[bar.t] = pos;
    usedBars.add(bar.t);
    usedPos.add(pos.entry_submitted_at_raw || pos.first_entry_time);
  });

  return result;
}

// ── Trade dot with hover tooltip ──────────────────────────────────────────────
function TradeDot({ pos, isWin }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  const color  = isWin ? C.green : C.red;
  const pnl    = pos.total_pnl_num ?? 0;
  const sign   = pnl >= 0 ? "+" : "";
  const time   = pos.entry_filled_at || pos.entry_submitted_at || "";
  const hold   = pos.hold_time || "";
  const exit   = (pos.exit_order_type || "").toLowerCase();
  const exitLabel = exit === "limit" ? "Target hit"
    : exit === "stop" || exit === "stop_limit" ? "Stop hit"
    : exit === "market" ? "Market close" : exit || "—";
  const dir = pos.direction || "";

  return (
    <span
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: "relative", display: "inline-flex" }}
    >
      <span style={{
        display: "inline-block",
        width: 8, height: 8, borderRadius: "50%",
        background: color,
        boxShadow: `0 0 4px ${color}80`,
        cursor: "default",
        flexShrink: 0,
      }} />
      {hovered && (
        <span style={{
          position: "absolute",
          bottom: "calc(100% + 6px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          background: C.s3,
          border: `1px solid ${C.b2}`,
          borderRadius: 8,
          padding: "8px 12px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
          display: "flex", flexDirection: "column", gap: 3,
        }}>
          <span style={{ fontFamily: MONO, fontSize: 9, color, fontWeight: 500 }}>
            {isWin ? "WIN" : "LOSS"} · {sign}${Math.abs(pnl).toFixed(2)}
          </span>
          {dir && <span style={{ fontFamily: MONO, fontSize: 8, color: C.body }}>{dir}</span>}
          {time && <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{time}</span>}
          {hold && <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>Hold: {hold}</span>}
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{exitLabel}</span>
        </span>
      )}
    </span>
  );
}


function RegimePill({ regime, count, proceed, skip }) {
  const cfg = getRegimeConfig(regime);
  const total = proceed + skip;
  const proceedPct = total > 0 ? Math.round((proceed / total) * 100) : 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: C.s3, border: `1px solid ${cfg.color}25`,
      borderRadius: 6, padding: "5px 10px",
    }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      <span style={{ fontFamily: MONO, fontSize: 9, color: cfg.color, minWidth: 80 }}>
        {cfg.label}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
        {count} eval{count !== 1 ? "s" : ""}
      </span>
      <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2, overflow: "hidden", minWidth: 40 }}>
        <div style={{
          width: `${proceedPct}%`, height: "100%",
          background: proceedPct > 60 ? C.green : proceedPct > 30 ? C.cyan : C.muted,
          borderRadius: 2, transition: "width 0.4s ease",
        }} />
      </div>
      <span style={{ fontFamily: MONO, fontSize: 9, color: C.body, minWidth: 36, textAlign: "right" }}>
        {proceed}P / {skip}S
      </span>
    </div>
  );
}

// ── Full regime row ───────────────────────────────────────────────────────────
function RegimeRow({ regime, count, proceed, skip, wins, losses, trades, isCurrentRegime }) {
  const cfg = getRegimeConfig(regime);
  const total      = proceed + skip;
  const proceedPct = total   > 0 ? (proceed / total) * 100 : 0;
  const skipPct    = total   > 0 ? (skip    / total) * 100 : 0;
  const hasOutcomes = trades?.length > 0;
  const winPct     = proceed > 0 ? (wins / proceed) * 100 : 0;
  const lossPct    = proceed > 0 ? (losses / proceed) * 100 : 0;

  // Behavioral correctness — now informed by outcomes when available
  let behaviorColor = C.muted;
  let behaviorLabel = "";
  if (hasOutcomes) {
    if (cfg.expectProceed === true  && winPct >= 60)  { behaviorColor = C.green; behaviorLabel = "Correct"; }
    if (cfg.expectProceed === true  && winPct <  60)  { behaviorColor = C.amber; behaviorLabel = "Underperforming"; }
    if (cfg.expectProceed === false && skipPct >= 60) { behaviorColor = C.green; behaviorLabel = "Correct"; }
    if (cfg.expectProceed === false && skipPct <  60) { behaviorColor = C.amber; behaviorLabel = "Aggressive"; }
  } else {
    if (cfg.expectProceed === true  && proceedPct >= 50) { behaviorColor = C.green; behaviorLabel = "Correct"; }
    if (cfg.expectProceed === true  && proceedPct <  50) { behaviorColor = C.amber; behaviorLabel = "Cautious"; }
    if (cfg.expectProceed === false && skipPct    >= 60) { behaviorColor = C.green; behaviorLabel = "Correct"; }
    if (cfg.expectProceed === false && skipPct    <  60) { behaviorColor = C.amber; behaviorLabel = "Aggressive"; }
  }

  return (
    <div style={{
      background: isCurrentRegime ? `${cfg.color}08` : C.s3,
      border: `1px solid ${isCurrentRegime ? cfg.color + "40" : C.border}`,
      borderRadius: 8, padding: "10px 14px",
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      {/* Top row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color }} />
          <span style={{ fontFamily: MONO, fontSize: 10, color: cfg.color, fontWeight: 500 }}>
            {cfg.label}
          </span>
          {isCurrentRegime && (
            <span style={{
              fontFamily: MONO, fontSize: 7, color: cfg.color,
              background: `${cfg.color}15`, border: `1px solid ${cfg.color}30`,
              borderRadius: 3, padding: "1px 5px",
            }}>
              CURRENT
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {behaviorLabel && (
            <span style={{ fontFamily: MONO, fontSize: 8, color: behaviorColor }}>
              {behaviorLabel}
            </span>
          )}
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>
            {count} eval{count !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Progress bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {/* PROCEED bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.green, width: 54 }}>
            PROCEED {proceed}
          </span>
          <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              width: `${proceedPct}%`, height: "100%",
              background: C.green, borderRadius: 2,
              transition: "width 0.5s ease",
            }} />
          </div>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.green, width: 28, textAlign: "right" }}>
            {Math.round(proceedPct)}%
          </span>
        </div>

        {/* Win/Loss sub-bar — only when outcome data available */}
        {hasOutcomes && proceed > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: 7, color: C.muted, width: 54 }}>
              └ OUTCOMES
            </span>
            {/* Segmented win/loss bar */}
            <div style={{
              flex: 1, height: 5, background: C.border, borderRadius: 2,
              overflow: "hidden", display: "flex",
            }}>
              <div style={{
                width: `${winPct}%`, height: "100%",
                background: C.green, transition: "width 0.5s ease",
              }} />
              <div style={{
                width: `${lossPct}%`, height: "100%",
                background: C.red, transition: "width 0.5s ease",
              }} />
            </div>
            {/* Win/loss counts */}
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, width: 28, textAlign: "right", whiteSpace: "nowrap" }}>
              <span style={{ color: C.green }}>{wins}W</span>
              <span style={{ color: C.muted }}>/</span>
              <span style={{ color: C.red }}>{losses}L</span>
            </span>
          </div>
        )}

        {/* Trade dots — hover for individual trade details */}
        {hasOutcomes && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, paddingLeft: 62, flexWrap: "wrap" }}>
            {trades.map((t, i) => (
              <TradeDot key={i} pos={t.pos} isWin={t.isWin} />
            ))}
          </div>
        )}

        {/* SKIP bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, width: 54 }}>
            SKIP {skip}
          </span>
          <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              width: `${skipPct}%`, height: "100%",
              background: C.muted, borderRadius: 2,
              transition: "width 0.5s ease",
            }} />
          </div>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, width: 28, textAlign: "right" }}>
            {Math.round(skipPct)}%
          </span>
        </div>
      </div>

      {/* Note */}
      <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, lineHeight: 1.5 }}>
        {cfg.note}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function RegimeDecisionTally({ liveBars = [], compact = false, showLabel = true, closedLedgerPositions = [] }) {

  const { buckets, currentRegime, totalEvals, totalProceed, totalSkip, totalWins, totalLosses } = useMemo(() => {
    // Build regime map from bar decisions
    const map = {};  // regime → { count, proceed, skip, proceedBars[], lastSeen }
    let currentRegime = null;
    let totalEvals = 0, totalProceed = 0, totalSkip = 0;

    (liveBars || []).forEach(bar => {
      const dec = bar.eval_decision || bar.decision;
      if (!dec || dec === "HOLD") return;

      const regimeRaw = extractRegime(bar);
      const regime = regimeRaw || "UNKNOWN";
      if (!map[regime]) map[regime] = { count: 0, proceed: 0, skip: 0, proceedBars: [], lastSeen: bar.t };

      map[regime].count++;
      totalEvals++;

      if (isProceed(bar)) {
        map[regime].proceed++;
        map[regime].proceedBars.push(bar);
        totalProceed++;
      } else if (isSkip(bar)) {
        map[regime].skip++;
        totalSkip++;
      }

      if (!currentRegime || (bar.t && bar.t > (map[currentRegime]?.lastSeen || ""))) {
        currentRegime = regime;
      }
      map[regime].lastSeen = bar.t || map[regime].lastSeen;
    });

    // Join closed positions to PROCEED bars per regime
    const joinMap = joinProceedsToClosed(liveBars, closedLedgerPositions);

    let totalWins = 0, totalLosses = 0;

    // Build final buckets with outcome data
    const buckets = Object.entries(map)
      .sort(([aKey, aVal], [bKey, bVal]) => {
        if (aKey === currentRegime) return -1;
        if (bKey === currentRegime) return 1;
        return bVal.count - aVal.count;
      })
      .map(([regime, vals]) => {
        // For each PROCEED bar in this regime, find its joined closed position
        const trades = vals.proceedBars
          .map(bar => {
            const pos = joinMap[bar.t];
            if (!pos) return null;
            const isWin = (pos.total_pnl_num ?? 0) > 0;
            return { pos, isWin };
          })
          .filter(Boolean);

        const wins   = trades.filter(t => t.isWin).length;
        const losses = trades.filter(t => !t.isWin).length;
        totalWins   += wins;
        totalLosses += losses;

        return { regime, count: vals.count, proceed: vals.proceed, skip: vals.skip, wins, losses, trades };
      });

    return { buckets, currentRegime, totalEvals, totalProceed, totalSkip, totalWins, totalLosses };
  }, [liveBars, closedLedgerPositions]);

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (buckets.length === 0) {
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>
          No evaluations yet — tally builds as session progresses
        </span>
      </div>
    );
  }

  // ── Compact layout ───────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 10, overflow: "hidden",
      }}>
        {showLabel && (
          <div style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: "1.2px" }}>
              REGIME TALLY · {totalEvals} EVALS
            </span>
          </div>
        )}
        <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
          {buckets.map(({ regime, count, proceed, skip }) => (
            <RegimePill
              key={regime}
              regime={regime}
              count={count}
              proceed={proceed}
              skip={skip}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Full layout ──────────────────────────────────────────────────────────────
  const currentCfg = getRegimeConfig(currentRegime);
  const overallProceedPct = totalEvals > 0 ? Math.round((totalProceed / totalEvals) * 100) : 0;

  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "10px 16px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: C.amber }}>⬡</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: "1.2px" }}>
            REGIME DECISION TALLY
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {/* Current regime */}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: currentCfg.color }} />
            <span style={{ fontFamily: MONO, fontSize: 8, color: currentCfg.color }}>
              {currentCfg.label}
            </span>
          </div>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
            {totalEvals} evals · {overallProceedPct}% proceed
          </span>
        </div>
      </div>

      {/* Session totals strip */}
      <div style={{
        padding: "8px 16px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>SESSION</span>
          <span style={{ fontFamily: MONO, fontSize: 11, color: C.green }}>{totalProceed} PROCEED</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>/</span>
          <span style={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>{totalSkip} SKIP</span>
          {(totalWins + totalLosses) > 0 && (<>
            <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>·</span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.green }}>{totalWins}W</span>
            <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>/</span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.red }}>{totalLosses}L</span>
          </>)}
        </div>
        {/* Overall bar */}
        <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
          <div style={{
            width: `${overallProceedPct}%`, height: "100%",
            background: overallProceedPct > 60 ? C.green : overallProceedPct > 30 ? C.cyan : C.muted,
            borderRadius: 2, transition: "width 0.5s ease",
          }} />
        </div>
        <span style={{ fontFamily: MONO, fontSize: 9, color: C.body }}>
          {overallProceedPct}%
        </span>
      </div>

      {/* Regime rows */}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {buckets.map(({ regime, count, proceed, skip, wins, losses, trades }) => (
          <RegimeRow
            key={regime}
            regime={regime}
            count={count}
            proceed={proceed}
            skip={skip}
            wins={wins}
            losses={losses}
            trades={trades}
            isCurrentRegime={regime === currentRegime}
          />
        ))}
      </div>

      {/* Footer interpretation */}
      <div style={{
        padding: "10px 16px",
        borderTop: `1px solid ${C.border}`,
        fontFamily: MONO, fontSize: 9, color: C.muted, lineHeight: 1.7,
      }}>
        {currentCfg.expectProceed === true
          ? `Bot is in ${currentCfg.label.toLowerCase()} conditions — expect active entry decisions`
          : currentCfg.expectProceed === false
          ? `Bot is in ${currentCfg.label.toLowerCase()} conditions — high skip rate is correct behavior`
          : "Regime classification in progress"
        }
      </div>
    </div>
  );
}